'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './helpers';
import React from 'react';

export type InputProps = Omit<React.ComponentProps<'input'>, 'size'> &
  VariantProps<typeof inputVariants> & {
    prefixIcon?: React.ReactNode;
    suffexIcon?: React.ReactNode;
    hasError?: boolean;
    inputClassName?: string;
  };

const inputVariants = cva(
  ' text-gray-800 group text-sm rounded-[8px] border-[1px] px-3 gap-2 outline-none outline-[1.5px] -outline-offset-[1.5px] transition-all has-disabled:cursor-not-allowed has-disabled:opacity-50 flex truncate disabled:cursor-not-allowed disabled:opacity-50 data-error:border-destructive data-error:z-10 data-error:ring-[3px] data-error:ring-destructive/15 data-error:focus-within:border-destructive data-error:focus-within:ring-destructive/15 focus-within:border-active-border focus-within:ring-ring/15 focus-within:ring-[3px]',

  {
    variants: {
      variant: {
        outline: 'border-pale-border',
        fill: 'border-pale-border bg-[#FAF9FC] focus-within:hover:border-active-border hover:border-primary-300',
      },
      size: {
        xs: 'h-[32px]',
        sm: 'h-[40px]',
        md: 'h-[44px]',
        lg: 'h-[48px]',
      },
    },
    defaultVariants: {
      variant: 'fill',
      size: 'lg',
    },
  }
);

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      hasError,
      size,
      variant,
      className,
      prefixIcon,
      suffexIcon,
      type,
      inputClassName,
      ...props
    },
    ref
  ) => {
    return (
      <div
        {...(hasError && { 'data-error': 'error' })}
        className={cn(inputVariants({ variant, size, className }))}
      >
        {prefixIcon && (
          <div className='flex items-center justify-center text-gray-200'>
            {prefixIcon}
          </div>
        )}
        <input
          type={type}
          ref={ref}
          className={cn(
            'h-full placeholder:text-gray-400 disabled:cursor-not-allowed placeholder:text-sm w-full border-none bg-[transparent] outline-none',
            inputClassName
          )}
          {...props}
        />
        {suffexIcon && (
          <div className='flex items-center justify-center text-gray-200'>
            {suffexIcon}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input, inputVariants };
